import { formatValue, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";

export function buildBoxplotOption({ query, data, palette, base, xKey }: ChartBuildCtx): ChartOption {
  const ms = query.measures;
  const yFmt = formatFromColumn(ms[0], data.columns);
  // Boxplot needs a 5-number summary (min, q1, median, q3, max) per
  // group. Two supported shapes:
  //   - measures already encode the summary (5 numeric columns in order)
  //   - one measure × multiple time buckets → group rows by category
  //     and compute the summary from the distribution of measure values.
  let rows: Array<[string, number[]]>;
  if (ms.length >= 5) {
    rows = data.rows.map((r) => {
      const name = String(r[xKey] ?? "");
      return [name, ms.slice(0, 5).map((m) => Number(r[m] ?? 0))] as [string, number[]];
    });
  } else {
    const measure = ms[0];
    const byCat = new Map<string, number[]>();
    for (const r of data.rows) {
      const name = String(r[xKey] ?? "");
      const v = Number(r[measure] ?? 0);
      if (!byCat.has(name)) byCat.set(name, []);
      byCat.get(name)!.push(v);
    }
    rows = Array.from(byCat.entries()).map(([name, vals]) => {
      vals.sort((a, b) => a - b);
      const n = vals.length;
      if (n === 0) return [name, [0, 0, 0, 0, 0]] as [string, number[]];
      const q = (p: number) => vals[Math.max(0, Math.min(n - 1, Math.floor(p * (n - 1))))];
      return [name, [vals[0], q(0.25), q(0.5), q(0.75), vals[n - 1]]] as [string, number[]];
    });
    // Sort by median descending so ranked.
    rows.sort((a, b) => b[1][2] - a[1][2]);
  }
  // Vary fill colour per box so the boxplot reads as N distinct
  // entities, not one homogeneous bar set. Whiskers stay neutral.
  const boxData = rows.map(([name, v], i) => ({
    name,
    value: v,
    itemStyle: { color: palette[i % palette.length], borderColor: "rgba(17,24,39,0.65)" },
  }));
  return {
    ...base,
    tooltip: {
      trigger: "item",
      formatter: (p: { name: string; value: number[] | string[] }) => {
        const [, min, q1, med, q3, max] = p.value as number[];
        return `<b>${p.name}</b><br/>min: ${formatValue(min, yFmt)}<br/>Q1: ${formatValue(q1, yFmt)}<br/>median: ${formatValue(med, yFmt)}<br/>Q3: ${formatValue(q3, yFmt)}<br/>max: ${formatValue(max, yFmt)}`;
      },
    },
    // Override base grid: rotated x-labels need a deeper bottom
    // margin than containLabel reserves by default, otherwise the
    // labels get clipped under the chart-card footer (caught in
    // eval-boxplot snapshot).
    grid: { left: 56, right: 16, top: 16, bottom: 56, containLabel: true },
    xAxis: {
      type: "category",
      data: rows.map(([n]) => n),
      axisLabel: {
        color: "#6b7280",
        fontSize: 11,
        rotate: 30,
        interval: 0,
        margin: 8,
        formatter: (v: string) => (typeof v === "string" && v.length > 14 ? v.slice(0, 13) + "…" : v),
      },
    },
    yAxis: {
      type: "value",
      axisLabel: { color: "#6b7280", fontSize: 11, formatter: (v: number) => formatValue(v, yFmt) },
    },
    series: [
      {
        type: "boxplot",
        data: boxData,
        // Tone down the whisker so it doesn't read as a separate
        // overlay; lift the box border so the IQR is the focal point.
        itemStyle: { borderWidth: 1.5 },
      },
    ],
  } as ChartOption;
}
